import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/brand/Logo";
import { SpecialistFigure, SpecialistNote } from "@/components/brand/Specialist";
import { flow } from "@/lib/flow";

const specialistLine = (p) => {
  const skin = (p.skin_type?.name || "").toLowerCase().replace("pelle ", "");
  const goal = (p.goals?.[0] || "").toLowerCase();
  return `Sono Sveta: ho letto le tue risposte, ${skin ? `la tua pelle è ${skin}` : "ho capito la tua pelle"}${goal ? ` e il tuo obiettivo è ${goal}` : ""}. Ti ho preparato un rituale giorno e sera su misura.`;
};
const fade = (d) => ({ initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d } });

const Row = ({ label, children, testid, d }) => (
  <motion.div {...fade(d)} className="grid grid-cols-3 gap-4 py-5 border-b border-[#E6DFD5]" data-testid={testid}>
    <span className="fs-eyebrow pt-1">{label}</span>
    <div className="col-span-2 font-serif text-lg leading-snug">{children}</div>
  </motion.div>
);

export default function SkinProfile() {
  const navigate = useNavigate();
  const [result] = useState(() => flow.result());
  useEffect(() => { if (!result) navigate("/", { replace: true }); }, [result, navigate]);
  if (!result) return null;
  const p = result.profile;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="skin-profile-page">
      <Header />
      <main className="max-w-6xl mx-auto px-6 sm:px-10 pb-20 grid lg:grid-cols-12 gap-12">
        <section className="lg:col-span-7 pt-6">
          <SpecialistNote text={specialistLine(p)} testid="profile-specialist-note" className="lg:hidden mb-8" />
          <motion.p {...fade(0)} className="fs-eyebrow">Il tuo Skin Profile</motion.p>
          <motion.h1 {...fade(0.1)} className="font-serif text-4xl sm:text-5xl leading-[1.1] tracking-tight mt-5">
            Ecco cosa abbiamo capito della tua pelle.
          </motion.h1>
          <motion.div {...fade(0.2)} className="mt-10 bg-[#F2ECE4] p-7 sm:p-9">
            <p className="fs-eyebrow">Tipo di pelle</p>
            <p data-testid="profile-skin-type" className="font-serif text-4xl mt-3">{p.skin_type?.name}</p>
            {p.skin_type?.deduced && (
              <p data-testid="profile-skin-deduced" className="text-xs text-[#8C6F3E] mt-2 tracking-wide">Dedotto dalle tue risposte sulle sensazioni della pelle</p>
            )}
            <p className="text-[15px] text-[#595652] leading-relaxed mt-5 font-light">{p.skin_type?.description}</p>
          </motion.div>
          <div className="mt-6">
            {p.sensitivity && <Row label="Sensibilità" testid="profile-sensitivity" d={0.3}>{p.sensitivity}</Row>}
            {p.goals.length > 0 && <Row label="Obiettivo" testid="profile-goals" d={0.35}>{p.goals.join(", ")}</Row>}
            {p.concerns.length > 0 && <Row label="Cosa noti" testid="profile-concerns" d={0.4}>{p.concerns.join(" · ")}</Row>}
            {p.experience && <Row label="Attivi" testid="profile-experience" d={0.42}>{p.experience}</Row>}
            {p.routine_mode?.length > 0 && <Row label="Routine" testid="profile-routine-mode" d={0.44}>{p.routine_mode.join(" · ")}</Row>}
            {p.notes?.length > 0 && <Row label="Usi oggi" testid="profile-notes" d={0.45}>{p.notes.map((n) => n.text).join(" · ")}</Row>}
          </div>
          {(p.conditions?.length > 0 || p.blocked_actives?.length > 0 || p.allergens?.length > 0) && (
            <motion.div {...fade(0.46)} className="mt-6 border border-[#E8DEC8] bg-white px-5 py-4" data-testid="profile-safety">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#8C6F3E]">Per la tua sicurezza</p>
              {p.conditions?.length > 0 && <p className="text-sm text-[#2B2A27] mt-2">Abbiamo tenuto conto di: {p.conditions.join(", ").toLowerCase()}.</p>}
              {p.allergens?.length > 0 && <p className="text-sm text-[#595652] mt-1" data-testid="profile-allergens">Ingredienti evitati per le tue allergie: {p.allergens.join(", ")}.</p>}
              {p.blocked_actives?.length > 0 && (
                <p className="text-sm text-[#595652] mt-1">Dalla tua routine abbiamo escluso {p.blocked_actives.map((b) => ({ retinoid: "retinoidi e spicole", exfoliant: "esfolianti (AHA/BHA/PHA, peeling)", vitc: "vitamina C" }[b] || b)).join(", ")}.</p>
              )}
            </motion.div>
          )}
          {p.needs.length > 0 && (
            <motion.div {...fade(0.5)} className="mt-12" data-testid="profile-needs">
              <p className="fs-eyebrow">Le esigenze della tua pelle</p>
              <ul className="mt-6 space-y-5">
                {p.needs.map((n, i) => (
                  <li key={n.id} className="flex gap-5">
                    <span className="font-serif italic text-[#C5A059]">{String(i + 1).padStart(2, "0")}</span>
                    <div><p className="font-serif text-lg">{n.name}</p><p className="text-sm text-[#8C8275] mt-1">{n.description}</p></div>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
          <motion.div {...fade(0.6)} className="mt-14 flex flex-col sm:flex-row sm:items-center gap-5">
            <button data-testid="profile-to-routine-btn" className="fs-btn" onClick={() => navigate("/routine")}>
              Scopri la mia routine <ArrowRight size={16} strokeWidth={1.5} />
            </button>
            <button data-testid="profile-redo-test-btn" className="fs-btn-ghost" onClick={() => { flow.restart(); navigate("/test"); }}>Rifai il test</button>
          </motion.div>
        </section>
        <motion.figure {...fade(0.2)} className="lg:col-span-5 hidden lg:block pt-10">
          <div className="sticky top-10"><SpecialistFigure text={specialistLine(p)} testid="profile-specialist-figure" /></div>
        </motion.figure>
      </main>
    </div>
  );
}
